// S2-CC20.6b.1.5: TreeNode — recursive collapsible node for the Budget Tree.
// Renders one BudgetNode row + (recursively) its children. Each row is the
// same 7-column grid specified in the wireframe:
//   chevron · scope_type Badge · scope_id · period tag · pct bar · lock · status pill
//
// Auto-expand heuristic: platform (depth 0) + role (depth 1) render open by
// default so operators see enforcement hierarchy without clicking. Depths
// 2+ (role_instance, agent_instance, job) default closed to avoid visual
// noise for deep trees.
//
// Wireframe: docs/sprint-2/wireframes/s2-cc6-llm-control-center.html
//   (.tree-node, .tree-row, .tree-chevron, .scope-badge.scope-*, .pct-bar-wrap, .lock-icon)
import { useState } from 'react';
import type { BudgetNode } from '../types/llm';
import { ProgressBar } from './ProgressBar';
import { StatusPill } from './StatusPill';
import './TreeNode.css';

export interface TreeNodeProps {
  node: BudgetNode;
  // 0-indexed depth. Drives the default open state and is threaded through
  // recursively so descendant nodes know how deep they are.
  depth?: number;
  // Explicit override. When provided, takes precedence over the depth
  // heuristic. Useful for tests + future "expand all" affordance.
  defaultOpen?: boolean;
}

// Map scope_type → a class suffix for the matching .scope-badge.scope-*
// rule in TreeNode.css. A 1:1 passthrough today, but keeping it as an
// indirection means future scope types can be added to the enum without
// touching the render path.
const SCOPE_CLASS: Record<BudgetNode['scope_type'], string> = {
  platform: 'scope-platform',
  role: 'scope-role',
  role_instance: 'scope-role_instance',
  agent_instance: 'scope-agent_instance',
  job: 'scope-job',
};

// Short display labels per scope_type — mirrors the wireframe badges
// ("Platform", "Role", "Role Inst", "Agent Inst", "Job"). Kept separate
// from the enum so display copy can change without touching contracts.
const SCOPE_LABEL: Record<BudgetNode['scope_type'], string> = {
  platform: 'Platform',
  role: 'Role',
  role_instance: 'Role Inst',
  agent_instance: 'Agent Inst',
  job: 'Job',
};

/**
 * Pick the ProgressBar variant from pct_used.
 *   ≤ 50%       → success (safe)
 *   > 50 – 80%  → warning (trending toward cap)
 *   > 80%       → error   (near breach / over budget)
 *
 * Note ProgressBar.tsx clamps the visual width at 100; callers rendering
 * pct_used > 100 must additionally color the text label red (see
 * pctLabelStyle below).
 */
function variantForPct(pct: number): 'success' | 'warning' | 'error' {
  if (pct <= 50) return 'success';
  if (pct <= 80) return 'warning';
  return 'error';
}

// Format cents as cents-with-a-decimal (e.g. 520 → "5.2¢", 19 → "19¢").
// Integer cents show no decimal; fractional cents show one. Matches the
// wireframe limit-label treatment.
function formatCents(cents: number): string {
  if (Number.isInteger(cents)) return `${cents}¢`;
  return `${cents.toFixed(1)}¢`;
}

export function TreeNode({ node, depth = 0, defaultOpen }: TreeNodeProps) {
  // Depth heuristic: auto-expand platform + role (0 + 1). Deeper defaults
  // closed. An explicit defaultOpen prop always wins.
  const initiallyOpen = defaultOpen ?? depth <= 1;
  const [open, setOpen] = useState(initiallyOpen);

  const hasChildren = node.children.length > 0;
  const isLeaf = !hasChildren;

  const variant = variantForPct(node.pct_used);
  // pct_used > 100 = warn-only breach; keep the bar error-variant AND
  // paint the numeric label red per wireframe (pct-label style override).
  const overBudget = node.pct_used > 100;

  // Container class: open/leaf flags mirror the wireframe's CSS hooks
  // (.tree-node.open, .tree-node.leaf) so existing styles transfer 1:1.
  const nodeClassName = [
    'airgent-tree-node',
    open ? 'airgent-tree-node-open' : '',
    isLeaf ? 'airgent-tree-node-leaf' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Chevron click toggles open/closed. No-op for leaf nodes (the chevron
  // is hidden via CSS, but we still guard the handler to prevent
  // accidental toggles if visibility is overridden).
  const handleChevronClick = () => {
    if (hasChildren) setOpen((v) => !v);
  };

  // Status pill variant mapping: the wireframe shows success for 'active'
  // budgets and warning for 'suspended'. 'suspended' is rendered using
  // StatusPill with 'gateway_paused' as a stand-in so we don't need a
  // new ENUM just for budget status — gateway_paused already maps to
  // the warning color group.
  const statusPillValue = node.status === 'active' ? 'success' : 'gateway_paused';

  return (
    <div className={nodeClassName}>
      <div className="airgent-tree-row">
        {/* Chevron — rotated 90deg when open (see TreeNode.css). Hidden for
            leaves via CSS visibility so the grid column stays reserved and
            sibling rows align across leaf + non-leaf. */}
        <span
          className="airgent-tree-chevron"
          onClick={handleChevronClick}
          // Only role=button for non-leaf chevrons; leaves get a plain span
          // because activating a hidden control is a confusing UX.
          role={hasChildren ? 'button' : undefined}
          aria-expanded={hasChildren ? open : undefined}
          aria-label={hasChildren ? (open ? 'Collapse node' : 'Expand node') : undefined}
          tabIndex={hasChildren ? 0 : undefined}
          onKeyDown={(e) => {
            if (!hasChildren) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
        >
          ▸
        </span>

        {/* scope_type badge — copper for agent_instance (signal), primary
            tints for platform/role/role_instance, neutral for job. Styling
            lives in TreeNode.css, not in the Badge component, because the
            4 primary tint steps are specific to this tree visualization. */}
        <span className={`airgent-scope-badge airgent-${SCOPE_CLASS[node.scope_type]}`}>
          {SCOPE_LABEL[node.scope_type]}
        </span>

        {/* scope_id — mono, agent-agnostic (no translated labels). */}
        <span className="airgent-scope-id">{node.scope_id}</span>

        {/* period_type — uppercase mono tag. */}
        <span className="airgent-period-tag">{node.period_type}</span>

        {/* pct_used bar + label. ProgressBar internally clamps width
            at 100; the label text additionally turns red when
            pct_used > 100 to signal warn-only breach. */}
        <div className="airgent-pct-bar-wrap">
          <div className="airgent-pct-track">
            <ProgressBar value={node.pct_used} variant={variant} size="sm" />
          </div>
          <span
            className="airgent-pct-label"
            style={overBudget ? { color: 'var(--error-dark-text)' } : undefined}
          >
            {Math.round(node.pct_used)}%{' '}
            <span className="airgent-pct-limit">
              · {formatCents(node.spent_cents)} / {formatCents(node.limit_cents)}
            </span>
          </span>
        </div>

        {/* Lock icon — 🔒 engaged (hard_stop=true), 🔓 open (warn-only). */}
        <span
          className={`airgent-lock-icon${node.hard_stop ? ' airgent-lock-icon-engaged' : ''}`}
          title={node.hard_stop ? 'hard_stop=true (enforced)' : 'hard_stop=false (warn-only)'}
        >
          {node.hard_stop ? '🔒' : '🔓'}
        </span>

        {/* Status pill — success for active budgets, warning for suspended. */}
        <StatusPill status={statusPillValue} size="sm" />
      </div>

      {/* Children wrapper — display is toggled via CSS based on the
          .airgent-tree-node-open class on the parent, so the wrapper
          always exists in the DOM (lets the dashed left-border rail
          render immediately on expand without a mount flash). */}
      {hasChildren && (
        <div className="airgent-tree-children">
          {node.children.map((child, i) => (
            // Key on scope_type+scope_id+period_start to avoid collisions
            // across siblings that share a scope_id (same agent, different
            // periods).
            <TreeNode
              key={`${child.scope_type}-${child.scope_id}-${child.period_start}-${i}`}
              node={child}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
